<template>
  <v-sheet
    color="white"
    elevation="5"
    rounded
    class="mt-5 mb-5 pt-3 pb-5 pl-3 pr-3"
    height="100%"
  >
        <v-overlay :value="loading">
          <v-progress-circular
            indeterminate
            color="primary"
            :size="70"
            :width="7"
          ></v-progress-circular>
        </v-overlay>
        <div>
          <v-row class="ma-0 mb-3 pb-3">          
            <h2>Individual Dog Registrations</h2>  
            <v-spacer></v-spacer>
            <v-btn color="primary" v-on:click="applyFilters">Apply Filters</v-btn
            >&nbsp;
            <v-btn color="secondary" v-on:click="clearAllFilters">Clear</v-btn>
        </v-row>
      </div>
        <v-row>
          <v-alert
            v-if="!!errorMessage"
            width="100%"
            class="mr-5 ml-5"
            v-model="errorMessage"
            dismissible
            type="error"
            >{{ errorMessage }}</v-alert
          >
        </v-row>
    <v-data-table 
      :loading="loading"
      :headers="headers"
      :options.sync="options"
      :items="paginatedResponse.data"
      :server-items-length="paginatedResponse.totalCount"
      :search="search"
      :footer-props="footerProps">
        <template v-slot:header.requester="{ header }">
          <div>
            <v-text-field
              v-model="searchParams.requesterName"
              label="Requester Name"
              clearable
              @click:clear="searchParams.requesterName = ''"
              v-on:keyup.enter="applyFilters"
            />
          </div>
          <v-text-field
            v-model="searchParams.requesterMemberId"
            label="Member ID"
            clearable
            @click:clear="searchParams.requesterMemberId = ''"
            v-on:keyup.enter="applyFilters"
          />
        </template>
        <template v-slot:header.officialName="{ header }">
          <div>
            <v-text-field
              v-model="searchParams.dogName"
              label="Dog Name"
              clearable
              @click:clear="searchParams.dogName = ''"
              v-on:keyup.enter="applyFilters"
            />
          </div>
        </template>
        <template v-slot:header.submittedAt="{ header }">
          <div width="300px">
            <datepicker 
              :date="searchParams.submittedDateFrom" 
              label="Submitted Date From" 
              v-on:updateDates="updateDates($event, 'submittedDateFrom')"
              v-on:enter="updateDateSearch($event, 'submittedDateFrom')"
            />
            <datepicker
              :date="searchParams.submittedDateTo"
              label="Submitted Date To"
              v-on:updateDates="updateDates($event, 'submittedDateTo')"
              v-on:enter="updateDateSearch($event, 'submittedDateTo')"
            />
          </div>
        </template>
        <template v-slot:header.birthDate="{ header }">
          <div width="300px">
            <datepicker 
              :date="searchParams.birthDateFrom" 
              label="Whelping Date From" 
              v-on:updateDates="updateDates($event, 'birthDateFrom')"
              v-on:enter="updateDateSearch($event, 'birthDateFrom')"
            />
            <datepicker
              :date="searchParams.birthDateTo"
              label="Whelping Date To"
              v-on:updateDates="updateDates($event, 'birthDateTo')"
              v-on:enter="updateDateSearch($event, 'birthDateTo')"
            />
          </div>
        </template>
        <template v-slot:header.status="{ header }">
          <RequestStatusesSelectMenu
            :menu.sync="menu"
            v-on:apply="applyFilters"
            v-on:updateStatuses="statuses = $event"
          />                
        </template>

        <template v-slot:item.requestId="{ item }">
          <router-link :to="`/admin/registration/dogs/individual/${item.requestId}`" target="_blank">{{
            item.requestId
          }}</router-link>
        </template>
        <template v-slot:item.submittedAt="{ item }">
          <span>{{ $dateUtils.formatDateTime(new Date(item.submittedAt)) }}</span>
        </template>
        <template v-slot:item.requester="{ item }">
          <span>{{ item.requester.lastName }}, {{ item.requester.firstName }}</span>
        </template>
        <template v-slot:item.birthDate="{ item }">
          <span>{{ $dateUtils.formatDateTime(new Date(item.birthDate)) }}</span>
        </template>
    </v-data-table>
  </v-sheet>
</template>

<script lang="ts">
import Vue from "vue";
import RequestStatusesSelectMenu from "@/components/common/inputs/RequestStatusesSelectMenu.vue";
import Datepicker from "@/components/common/inputs/DatepickerFormatted.vue";

import { PaginationOptions, PaginatedResponse } from "@/types/Pagination";
import workflowService from '@/services/admin/workflowService';

export default Vue.extend({
  name: "DogRegistrationIndividualList",
  components: { RequestStatusesSelectMenu, Datepicker },
  props: {
    readyToApproveOnly: String,
  },
  data: function () {
    return {
      loading: false,
      paginatedResponse: {} as PaginatedResponse<object>,
      menu: false,
      search: '',
      statuses: ["RequestFinalized"],
      options: {} as PaginationOptions,
      headers: [
        { text: "#ID", align: "left", value: "requestId" },
        { text: "Submitted at", align: "left", value: "submittedAt" },
        { text: "Submitted by", align: "left", value: "requester", sortable: false },
        { text: "Status", align: "left", value: "status" },
        { text: "Official Name", align: "left", value: "officialName", sortable: false },
        { text: "Date of Birth", align: "left", value: "birthDate", sortable: false },
      ],
      footerProps: { 'items-per-page-options': [10, 20, 30, 40, 50] },
      errorMessage: null as string | null,
      searchParams: {
        requesterName: "",
        requesterMemberId: "",
        dogName: "",
        submittedDateFrom: "",
        submittedDateTo: "",
        birthDateFrom: "",
        birthDateTo: "",
        statuses: "RequestFinalized",
      },
    };
  },
  created() { this.loadList(); },
  watch: {
    options: {
      handler() {
        this.loadList();
      },
      deep: true,
    }
  },
  methods: {
    applyFilters: function () {
      this.menu = false;
      this.loadList();
    },
    clearAllFilters: function () {
      this.options.page = 1;
      this.options.itemsPerPage = 10;
      this.searchParams = {
        requesterName: "",
        requesterMemberId: "",
        dogName: "",
        submittedDateFrom: "",
        submittedDateTo: "",
        birthDateFrom: "",
        birthDateTo: "",
        statuses: "RequestFinalized",
      };

    },
    loadList: async function () {
      this.loading = true;
      const {
        page,
        itemsPerPage,
      } = this.options;

      try {        
        this.searchParams.statuses = this.statuses.join('|');
        const response = await workflowService.searchDogRegistrationsIndividualList({
          ...this.options,
          page: (page ?? 1) - 1,
          itemsPerPage: itemsPerPage ?? 10
        }, this.searchParams);

        this.loading = false;
        this.paginatedResponse = response;
      } catch (ex: any) {
        this.loading = false;

        const errors = (ex?.response ?? ex)?.data?.errors;
        const descriptiveMessage = errors
          ? `Error(s): ${Object.values(errors).join(" & ")}`
          : null

        this.errorMessage = descriptiveMessage ?? ex?.message ?? "Unknown error occurred when retrieving data.";
      }
    },
    updateDates: function (val: any, key: string) {
      (this as any).searchParams[key] = val.date;
    },
    updateDateSearch: async function(val: any, key: string) {
      await this.updateDates(val, key);
      this.applyFilters();
    },
  },
});
</script>
